import {
    Component,
    ComponentInterface,
    Prop,
    State,
    Watch,
} from "@stencil/core";
import api from "../../utils/api";
import renderOrderRow from "./row";
import serializeForm from "../../utils/serializeForm";
import waitForHydrated from "../../utils/waitForHydrated";
import { MyOrdersItem, MyOrdersResponse } from "./my-orders-response";

@Component({
    tag: "ux-iws-data-controller-my-orders",
    styleUrl: "data-controller-my-orders.scss",
})
export class DataControllerMyOrders implements ComponentInterface {
    /** endpoint where data can be requested */
    @Prop() url: string = "";
    /** CSS-style selector for the scroll-sensor */
    @Prop() scrollSensorSelector: string = "";
    /** CSS-style selector for the table */
    @Prop() tableSelector: string = "";
    /** CSS-style selector for the error block */
    @Prop() errorSelector: string = "";
    /** CSS-style selector for the date form */
    @Prop() dateSelector: string = "";
    /** CSS-style selector for the filter */
    @Prop() filterSelector: string = "";
    /** CSS-style selector for the element where the total number of machines should be placed */
    @Prop() totalNumberSelector: string = "";
    /** CSS-style selector for the element where the number of filtered machines should be placed */
    @Prop() filteredNumberSelector: string = "";
    /** error message to be shown when the component encounters fetching problems */
    @Prop() errorMessage: string = "No orders found.";
    /** element that contains all filters */
    private filter: HTMLUxFilterElement | null = null;
    // /** URL for getting the next page */
    private nextPage: string = "";
    /** table element */
    private table: HTMLTableElement | null = null;
    /** table body element where the data should be put */
    private tableBody: HTMLTableSectionElement | null = null;
    /** form element for filter */
    private filterForm: HTMLFormElement | null = null;
    /** div element for error logging */
    private errorBlock: HTMLDivElement | null = null;
    /** form element for filter */
    private dateForm: HTMLFormElement | null = null;
    /** DOM element where all the tiles should be put */
    private scrollSensor: HTMLUxInfiniteScrollSensorElement | null = null;
    /** NodeList of DOM elements where the number of filtered machines should be put */
    private filteredNumberElements: NodeList | null = null;
    /** NodeList of DOM elements where the total number of machines should be put */
    private totalNumberElements: NodeList | null = null;
    private sorting: string = "";
    /** modal spinner element */
    private spinner = null;
    /** inital request */
    private initialRequestState: boolean = true;

    /** error state */
    @State() isError: boolean = false;

    componentDidLoad() {
        this.setFilter();
        this.setFilteredNumberElements();
        this.setTable();
        this.setTotalNumberElements();
        this.setSpinnerOverlay();
        this.setDateFilter();
        this.setScrollSensor();
        this.requestData(this.url);
        this.setErrorBlock();
    }

    private setSpinnerOverlay() {
        if (this.spinner === null) {
            this.spinner = document.createElement("ux-spinner-overlay");
            document.body.append(this.spinner);
        }
        this.spinner.opened = true;
    }

    private setDateFilter() {
        this.dateForm = document.querySelector(
            this.dateSelector
        ) as HTMLFormElement;

        const picker = this.dateForm.querySelector("ux-date-range-picker");

        if (picker !== null) {
            picker.addEventListener("uxDateRangeChange", () => {
                setTimeout(() => {
                    this.requestData(this.url);
                }, 100);
            });
        }
    }

    private setErrorBlock() {
        this.errorBlock = document.querySelector(
            this.errorSelector
        ) as HTMLDivElement;
        // find class my-orders-overview__failure-text in errrorBlock and set contents to errorMessage
        this.errorBlock.querySelector(
            ".my-orders-overview__failure-text h4"
        ).textContent = this.errorMessage;
    }

    private updateNumbers(total: number): void {
        writeToElements(this.totalNumberElements, total.toString());
    }

    private setFilterForm() {
        this.filterForm = this.filter.querySelector("form") as HTMLFormElement;
        this.filterForm?.addEventListener("submit", (e) => e.preventDefault());
    }

    /** connect filter */
    @Watch("filterSelector")
    setFilter() {
        if (this.filterSelector === "") return;
        this.filter = document.querySelector(
            this.filterSelector
        ) as HTMLUxFilterElement;

        if (this.filter === null) return;
        waitForHydrated(this.filter, () => this.setFilterForm());
        this.filter.addEventListener("uxFilterChange", () => {
            this.requestData(this.url);
        });
    }

    /** connect table */
    @Watch("tableSelector")
    setTable() {
        if (this.tableSelector === "") return;
        this.table = document.querySelector(
            this.tableSelector
        ) as HTMLTableElement;
        this.tableBody = this.table.querySelector("tbody");
        this.table.addEventListener("uxTableSort", (e: CustomEvent) => {
            const { name } = e.detail;
            if (name === "") return;
            this.sorting = name;
            this.requestData(this.url);
        });
    }

    /** connect filtered number */
    @Watch("filteredNumberSelector")
    setFilteredNumberElements() {
        if (this.filteredNumberSelector === "") return;
        this.filteredNumberElements = document.querySelectorAll(
            this.filteredNumberSelector
        );
    }

    /** connect total number */
    @Watch("totalNumberSelector")
    setTotalNumberElements() {
        if (this.totalNumberSelector === "") return;
        this.totalNumberElements = document.querySelectorAll(
            this.totalNumberSelector
        );
    }

    private appendItemsToTable(
        items: MyOrdersItem[],
        response?: MyOrdersResponse["multipleMachines"]
    ) {
        if (this.tableBody === null) return;
        this.tableBody.insertAdjacentHTML(
            "beforeend",
            items.map((item) => renderOrderRow(item, response)).join("")
        );
        let numItems = this.tableBody.querySelectorAll("tbody tr").length;
        writeToElements(this.filteredNumberElements, numItems.toString());
    }

    // private createTableRow(item: string) {}

    /** connect scroll-sensor */
    @Watch("scrollSensorSelector")
    setScrollSensor() {
        if (this.scrollSensorSelector === "") return;
        this.scrollSensor = document.querySelector(
            this.scrollSensorSelector
        ) as HTMLUxInfiniteScrollSensorElement;
        if (this.scrollSensor !== null) {
            this.scrollSensor.addEventListener(
                "uxInfiniteScrollSensorScrolledIntoView",
                () => {
                    this.requestData(this.nextPage);
                }
            );
        }
    }

    /** post filter and sort settings to get data from the endpoint */
    @Watch("url")
    initialRequest() {
        this.requestData(this.url);
    }

    private clearExistingData() {
        if (this.tableBody !== null) this.tableBody.textContent = "";
    }

    /** Send the real request and handle response */
    private requestData(url: string) {
        if (url === "") return;
        if (url === this.url) this.clearExistingData();
        this.spinner.opened = true;

        const body: string = this.initialRequestState
            ? "filters=restoreFromSession"
            : (this.sorting !== "" ? `sort=${this.sorting}&` : "") +
              serializeForm(this.dateForm) +
              (this.filterForm !== null
                  ? "&" + serializeForm(this.filterForm)
                  : "");
        if (isMockUrl(url)) {
            console.log("filter:", body);
        }

        const options = isMockUrl(url)
            ? {}
            : {
                  method: "POST",
                  body,
                  headers: {
                      "Content-Type":
                          "application/x-www-form-urlencoded;charset=UTF-8",
                  },
              };

        try {
            api<MyOrdersResponse>(url, options)
                .then((data) => {
                    if (data.error === 1) {
                        this.isError = true;
                        this.handleContent(data.message);
                        return false;
                    } else {
                        this.isError = false;
                    }
                    this.replaceFilter(data.filterHTML);
                    this.appendItemsToTable(
                        data.items,
                        data.multipleMachines ?? null
                    );
                    this.nextPage = data.nextPage;
                    this.updateNumbers(data.totalCount);
                    if (this.nextPage !== "" && this.scrollSensor !== null) {
                        this.scrollSensor.hasFired = false;
                    }
                })
                .finally(() => {
                    if (!this.isError) {
                        this.handleContent();
                    }
                    this.spinner.opened = false;
                    this.initialRequestState = false;
                });
        } catch (e) {
            console.log(e);
            this.spinner.opened = false;
        }
    }

    private handleContent(message?: string) {
        if (message !== undefined) {
            console.log("message: ", message);
        }
        let containers = document.querySelectorAll<HTMLElement>(
            ".my-orders-overview .u-background-blue-gradient > *"
        );
        containers.forEach((elm) => {
            if (this.isError) {
                // If there's an error, show the error block and hide others
                elm === this.errorBlock
                    ? (elm.style.display = "block")
                    : (elm.style.display = "none");
            } else {
                // If there's no error, show all elements except the error block
                elm === this.errorBlock
                    ? (elm.style.display = "none")
                    : (elm.style.display = "block");
            }
        });
    }

    private replaceFilter(html: string | undefined) {
        if (
            typeof html === "string" &&
            html.length > 0 &&
            this.filter !== null
        ) {
            const parent = this.filter.parentElement;
            if (parent !== null) {
                parent.style.height = `${parent.offsetHeight}px`;
            }
            this.filter.outerHTML = html;
            this.setFilter();
            setTimeout(() => {
                parent.style.height = "auto";
            }, 200);
        }
    }

    render() {
        return null;
    }
}

function writeToElements(nodes: NodeList | null, text: string): void {
    if (nodes === null) return;
    [].forEach.call(
        nodes,
        (element: HTMLElement) => (element.textContent = text)
    );
}

function isMockUrl(url: string) {
    return url.indexOf("mock-data/") > -1;
}
